import { useState } from 'react';
import { CoachLayout } from '@/components/coach/layout/CoachLayout';
import { sportConfig } from '@/lib/coachConstants';
import type { DayOfWeek, SportType } from '@/lib/coachConstants';
import { useWorkoutTemplates } from '@/hooks/useWorkoutTemplates';
import {
  useWeekTemplates,
  useCreateWeekTemplate,
  useUpdateWeekTemplate,
  useDeleteWeekTemplate,
  useDuplicateWeekTemplate,
} from '@/hooks/useWeekTemplates';
import type { WeekTemplateResponse } from '@/lib/templateApi';
import { cn } from '@/lib/utils';
import { Plus, Copy, Trash2, Pencil, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/feedback/alert-dialog';

const DAYS: DayOfWeek[] = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const sportOptions: SportType[] = ['running', 'cycling', 'swimming', 'strength', 'crossfit', 'hyrox', 'multi-sport'];

const DEFAULT_DAYS: Record<DayOfWeek, string[]> = {
  Mon: [], Tue: [], Wed: [], Thu: [], Fri: [], Sat: [], Sun: [],
};

export default function WeekBuilderPage() {
  const { data: weekData, isLoading } = useWeekTemplates();
  const weekTemplates: WeekTemplateResponse[] = weekData?.items ?? [];

  const createMutation = useCreateWeekTemplate();
  const updateMutation = useUpdateWeekTemplate();
  const deleteMutation = useDeleteWeekTemplate();
  const duplicateMutation = useDuplicateWeekTemplate();

  const { data: workoutData } = useWorkoutTemplates();
  const workoutTemplates = workoutData?.items ?? [];

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [addingToDay, setAddingToDay] = useState<DayOfWeek | null>(null);
  // Local draft for metadata edits — kept in sync with selected template
  const [draft, setDraft] = useState<Partial<WeekTemplateResponse>>({});

  const selected = weekTemplates.find(w => w.id === selectedId);

  const handleCreate = async () => {
    try {
      const wt = await createMutation.mutateAsync({ name: 'New Week', sport: 'running', days: { ...DEFAULT_DAYS } });
      setSelectedId(wt.id);
      setDraft({ name: wt.name, sport: wt.sport, phase: wt.phase, target_volume: wt.target_volume, intensity_distribution: wt.intensity_distribution, notes: wt.notes });
      setEditing(true);
      toast.success('New week template created');
    } catch {
      toast.error('Failed to create week template');
    }
  };

  const handleDuplicate = async () => {
    if (!selected) return;
    try {
      const copy = await duplicateMutation.mutateAsync(selected.id);
      setSelectedId(copy.id);
      toast.success(`Duplicated as "${copy.name}"`);
    } catch {
      toast.error('Failed to duplicate');
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    const name = selected.name;
    try {
      await deleteMutation.mutateAsync(selected.id);
      setSelectedId(weekTemplates.filter(w => w.id !== selected.id)[0]?.id ?? null);
      setEditing(false);
      toast.success(`"${name}" deleted`);
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleSaveEdit = async () => {
    if (!selected) return;
    try {
      await updateMutation.mutateAsync({ id: selected.id, payload: draft });
      setEditing(false);
      toast.success('Saved');
    } catch {
      toast.error('Failed to save');
    }
  };

  const handleAddWorkout = async (day: DayOfWeek, templateId: string) => {
    if (!selected) return;
    const currentDays = selected.days as Record<string, string[]>;
    const newDays = { ...currentDays, [day]: [...(currentDays[day] ?? []), templateId] };
    try {
      await updateMutation.mutateAsync({ id: selected.id, payload: { days: newDays } });
      const tmpl = workoutTemplates.find(t => t.id === templateId);
      toast.success(`Added "${tmpl?.name}" to ${day}`);
      setAddingToDay(null);
    } catch {
      toast.error('Failed to add workout');
    }
  };

  const handleRemoveWorkout = async (day: DayOfWeek, index: number) => {
    if (!selected) return;
    const currentDays = selected.days as Record<string, string[]>;
    const newDayList = [...(currentDays[day] ?? [])];
    newDayList.splice(index, 1);
    try {
      await updateMutation.mutateAsync({ id: selected.id, payload: { days: { ...currentDays, [day]: newDayList } } });
      toast.success('Workout removed');
    } catch {
      toast.error('Failed to remove workout');
    }
  };

  if (isLoading) {
    return (
      <CoachLayout>
        <div className="flex items-center justify-center h-48">
          <p className="text-xs text-muted-foreground font-mono">Loading week templates...</p>
        </div>
      </CoachLayout>
    );
  }

  return (
    <CoachLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-foreground">Week Template Builder</h1>
            <p className="text-xs text-muted-foreground mt-0.5">Design reusable weekly training patterns</p>
          </div>
          <Button size="sm" className="gap-1.5 bg-accent/90 hover:bg-accent text-accent-foreground text-xs font-mono uppercase tracking-wider"
            onClick={handleCreate} disabled={createMutation.isPending}>
            <Plus className="h-3.5 w-3.5" /> New Week
          </Button>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {/* Sidebar */}
          <div className="col-span-12 lg:col-span-3">
            <div className="rounded-lg border border-border/15 bg-card/20 overflow-hidden">
              <div className="px-4 py-3 text-[10px] font-mono uppercase tracking-wider text-muted-foreground border-b border-border/10">
                Week Templates ({weekTemplates.length})
              </div>
              <div className="p-2 space-y-1 max-h-[600px] overflow-y-auto">
                {weekTemplates.map((wt) => {
                  const sport = sportConfig[wt.sport as SportType];
                  const workoutCount = Object.values(wt.days as Record<string, string[]>).flat().length;
                  return (
                    <button key={wt.id} onClick={() => { setSelectedId(wt.id); setEditing(false); setAddingToDay(null); }}
                      className={cn('w-full text-left rounded-lg border p-3 transition-all',
                        wt.id === selectedId ? 'border-accent/40 bg-accent/5' : 'border-border/15 bg-card/30 hover:bg-card/50')}>
                      <p className="text-xs font-medium text-foreground truncate">{wt.name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {sport && <span className="text-[8px] font-mono uppercase px-1.5 py-0.5 rounded" style={{ background: `${sport.color}15`, color: sport.color }}>{sport.label}</span>}
                        <span className="text-[10px] text-muted-foreground">{workoutCount} sessions</span>
                      </div>
                      {wt.phase && <p className="text-[10px] text-muted-foreground/70 mt-1">{wt.phase} phase</p>}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Main */}
          <div className="col-span-12 lg:col-span-9 space-y-4">
            {selected ? (
              <>
                {/* Header */}
                <div className="rounded-lg border border-border/15 bg-card/20 p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {editing ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                          <div className="space-y-1 sm:col-span-2">
                            <label className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground">Name</label>
                            <input type="text" value={draft.name ?? selected.name}
                              onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
                              className="w-full text-xs bg-background/50 border border-border/20 rounded px-2 py-1.5 text-foreground focus:border-accent/40 focus:outline-none" />
                          </div>
                          <div className="space-y-1">
                            <label className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground">Sport</label>
                            <select value={draft.sport ?? selected.sport}
                              onChange={e => setDraft(d => ({ ...d, sport: e.target.value }))}
                              className="w-full text-xs bg-background/50 border border-border/20 rounded px-2 py-1.5 text-foreground focus:border-accent/40 focus:outline-none capitalize">
                              {sportOptions.map(s => <option key={s} value={s}>{sportConfig[s].label}</option>)}
                            </select>
                          </div>
                          <div className="space-y-1">
                            <label className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground">Phase</label>
                            <input type="text" value={draft.phase ?? selected.phase ?? ''}
                              onChange={e => setDraft(d => ({ ...d, phase: e.target.value || undefined }))}
                              className="w-full text-xs bg-background/50 border border-border/20 rounded px-2 py-1.5 text-foreground focus:border-accent/40 focus:outline-none" placeholder="e.g. Base" />
                          </div>
                          <div className="space-y-1">
                            <label className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground">Target Volume</label>
                            <input type="text" value={draft.target_volume ?? selected.target_volume ?? ''}
                              onChange={e => setDraft(d => ({ ...d, target_volume: e.target.value || undefined }))}
                              className="w-full text-xs bg-background/50 border border-border/20 rounded px-2 py-1.5 text-foreground focus:border-accent/40 focus:outline-none" placeholder="e.g. 60-70 km" />
                          </div>
                          <div className="space-y-1">
                            <label className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground">Intensity Dist.</label>
                            <input type="text" value={draft.intensity_distribution ?? selected.intensity_distribution ?? ''}
                              onChange={e => setDraft(d => ({ ...d, intensity_distribution: e.target.value || undefined }))}
                              className="w-full text-xs bg-background/50 border border-border/20 rounded px-2 py-1.5 text-foreground focus:border-accent/40 focus:outline-none" placeholder="e.g. 80/20" />
                          </div>
                          <div className="space-y-1 col-span-2 sm:col-span-3">
                            <label className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground">Notes</label>
                            <textarea value={draft.notes ?? selected.notes ?? ''}
                              onChange={e => setDraft(d => ({ ...d, notes: e.target.value || undefined }))}
                              className="w-full text-xs bg-background/50 border border-border/20 rounded px-2 py-1.5 text-foreground focus:border-accent/40 focus:outline-none resize-none h-14" placeholder="Week notes..." />
                          </div>
                        </div>
                      ) : (
                        <div>
                          <h2 className="text-lg font-medium text-foreground">{selected.name}</h2>
                          <div className="flex items-center gap-3 mt-1 flex-wrap">
                            {sportConfig[selected.sport as SportType] && (
                              <span className="text-[9px] font-mono uppercase tracking-wider px-2 py-0.5 rounded"
                                style={{ background: `${sportConfig[selected.sport as SportType].color}20`, color: sportConfig[selected.sport as SportType].color }}>
                                {sportConfig[selected.sport as SportType].label}
                              </span>
                            )}
                            {selected.phase && <span className="text-xs text-muted-foreground">{selected.phase} phase</span>}
                            {selected.target_volume && <span className="text-xs text-muted-foreground">Vol: {selected.target_volume}</span>}
                            {selected.intensity_distribution && <span className="text-xs text-muted-foreground">Int: {selected.intensity_distribution}</span>}
                          </div>
                          {selected.notes && <p className="text-xs text-muted-foreground/80 mt-2">{selected.notes}</p>}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1.5 ml-3 flex-shrink-0">
                      <Button variant="ghost" size="icon" className={cn('h-7 w-7', editing ? 'text-accent' : 'text-muted-foreground hover:text-foreground')}
                        onClick={() => { if (editing) { handleSaveEdit(); } else { setDraft({}); setEditing(true); } }}
                        disabled={updateMutation.isPending}>
                        {editing ? <Check className="h-3.5 w-3.5" /> : <Pencil className="h-3.5 w-3.5" />}
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground"
                        onClick={handleDuplicate} disabled={duplicateMutation.isPending}>
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-card border-border/30">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="text-foreground">Delete Week Template?</AlertDialogTitle>
                            <AlertDialogDescription className="text-muted-foreground">
                              <span className="font-medium text-foreground">"{selected.name}"</span>
                              <br />This will permanently delete the week template.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="text-xs font-mono uppercase tracking-wider">Cancel</AlertDialogCancel>
                            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90 text-xs font-mono uppercase tracking-wider"
                              onClick={handleDelete}>Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </div>

                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-2">
                  {DAYS.map(day => {
                    const days = selected.days as Record<string, string[]>;
                    const workoutIds: string[] = days[day] ?? [];
                    const dayWorkouts = workoutIds.map(id => workoutTemplates.find(t => t.id === id)).filter(Boolean);
                    return (
                      <div key={day} className="rounded-lg border border-border/15 bg-card/20 min-h-[160px] flex flex-col group">
                        <div className="flex items-center justify-between px-2 py-2 border-b border-border/10">
                          <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{day}</span>
                          <Button variant="ghost" size="icon" className="h-5 w-5 text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => setAddingToDay(addingToDay === day ? null : day)}>
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                        <div className="p-1.5 space-y-1 flex-1">
                          {dayWorkouts.map((t, idx) => {
                            if (!t) return null;
                            const sport = sportConfig[t.sport as SportType];
                            return (
                              <div key={`${t.id}-${idx}`} className="rounded-md border px-2 py-1.5 group/item"
                                style={{ borderColor: `${sport?.color ?? '#888'}25`, background: `${sport?.color ?? '#888'}08` }}>
                                <div className="flex items-start justify-between">
                                  <div className="min-w-0 flex-1">
                                    <p className="text-[10px] font-medium text-foreground truncate">{t.name}</p>
                                    <div className="flex items-center gap-1 mt-0.5">
                                      <div className="h-1.5 w-1.5 rounded-full" style={{ background: sport?.color ?? '#888' }} />
                                      <span className="text-[8px] text-muted-foreground">{t.duration}</span>
                                    </div>
                                  </div>
                                  <button onClick={() => handleRemoveWorkout(day, idx)}
                                    className="opacity-0 group-hover/item:opacity-100 text-muted-foreground hover:text-destructive p-0.5 transition-opacity">
                                    <X className="h-2.5 w-2.5" />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                          {dayWorkouts.length === 0 && (
                            <div className="flex-1 flex items-center justify-center h-full min-h-[80px]">
                              <span className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground/30">Rest</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Add workout picker */}
                {addingToDay && (
                  <div className="rounded-lg border border-accent/20 bg-card/30 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Add workout to {addingToDay}</p>
                      <Button variant="ghost" size="sm" className="text-[10px] text-muted-foreground" onClick={() => setAddingToDay(null)}>Cancel</Button>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                      {workoutTemplates.map(t => {
                        const sport = sportConfig[t.sport as SportType];
                        return (
                          <button key={t.id} onClick={() => handleAddWorkout(addingToDay, t.id)}
                            className="text-left rounded-md border border-border/15 px-3 py-2 hover:bg-accent/5 hover:border-accent/30 transition-all">
                            <p className="text-[11px] text-foreground font-medium truncate">{t.name}</p>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              {sport && <span className="text-[8px] font-mono uppercase" style={{ color: sport.color }}>{sport.label}</span>}
                              <span className="text-[9px] text-muted-foreground">{t.duration}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Week Summary */}
                <div className="rounded-lg border border-border/15 bg-card/20 p-4">
                  <h3 className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-3">Week Summary</h3>
                  <div className="flex flex-wrap gap-4">
                    {Object.entries(sportConfig).map(([key, config]) => {
                      const days = selected.days as Record<string, string[]>;
                      const count = Object.values(days).flat().filter(id => workoutTemplates.find(t => t.id === id)?.sport === key).length;
                      if (count === 0) return null;
                      return (
                        <div key={key} className="flex items-center gap-1.5">
                          <div className="h-2 w-2 rounded-full" style={{ background: config.color }} />
                          <span className="text-[10px] text-muted-foreground">{config.label}: {count}</span>
                        </div>
                      );
                    })}
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] text-muted-foreground">
                        Rest days: {DAYS.filter(d => ((selected.days as Record<string, string[]>)[d] ?? []).length === 0).length}
                      </span>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="rounded-lg border border-border/15 bg-card/20 p-12 text-center">
                <p className="text-sm text-muted-foreground">Select a week template or create a new one</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </CoachLayout>
  );
}
